'use client';
import React, { useState, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { CheckCircle2, ChevronRight, ChevronLeft, User, Store, Briefcase, Building2, Users, Home, Hotel, Wrench, Eye, EyeOff, Phone, Mail, Lock, MapPin, Globe, Download, ArrowRight } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


type AccountPurpose = 'buyer' | 'seller' | 'job_seeker' | 'employer' | 'agent' | 'property' | 'hotel' | 'services';

interface FormData {
  purpose: AccountPurpose | null;
  fullName: string;
  phone: string;
  email: string;
  password: string;
  confirmPassword: string;
  country: string;
  location: string;
  acceptTerms: boolean;
  // Role-specific
  companyName?: string;
  jobTitle?: string;
  bio?: string;
  skills?: string;
  experience?: string;
}

const purposeOptions = [
  { id: 'buyer', icon: Home, label: 'I want to buy or browse', desc: 'Shop products, find services, browse listings', color: '#00C853' },
  { id: 'seller', icon: Store, label: 'I want to sell', desc: 'List products, manage orders, grow your shop', color: '#FFB300' },
  { id: 'job_seeker', icon: Briefcase, label: 'I am a job seeker', desc: 'Find jobs, upload CV, track applications', color: '#7C4DFF' },
  { id: 'employer', icon: Building2, label: 'I am an employer', desc: 'Post jobs, find talent, manage hiring', color: '#00BCD4' },
  { id: 'agent', icon: Users, label: 'I am a recruitment agent', desc: 'Place candidates, manage clients', color: '#FF6D00' },
  { id: 'property', icon: Home, label: 'I want to list property', desc: 'Rent or sell properties', color: '#E040FB' },
  { id: 'hotel', icon: Hotel, label: 'I want to list a hotel', desc: 'Manage rooms, bookings, guests', color: '#FF4081' },
  { id: 'services', icon: Wrench, label: 'I want to offer services', desc: 'Freelance, consulting, skilled work', color: '#26C6DA' },
];

const STEPS = ['Purpose', 'Account', 'Profile', 'Done'];

function OnboardingContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { signUp } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState<FormData>({
    purpose: null,
    fullName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    country: 'Kenya',
    location: '',
    acceptTerms: false,
  });

  const update = (field: keyof FormData, value: any) => setForm(prev => ({ ...prev, [field]: value }));

  const handleNext = async () => {
    if (step === 1 && !form.purpose) {
      toast.error('Please select your account purpose');
      return;
    }
    if (step === 2) {
      if (!form.fullName.trim()) { toast.error('Full name is required'); return; }
      if (!form.email.trim()) { toast.error('Email is required'); return; }
      if (!form.password || form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
      if (form.password !== form.confirmPassword) { toast.error('Passwords do not match'); return; }
      if (!form.acceptTerms) { toast.error('Please accept the terms and conditions'); return; }

      setLoading(true);
      try {
        await signUp(form.email, form.password, {
          fullName: form.fullName,
          role: form.purpose,
          phone: form.phone,
          country: form.country,
          location: form.location,
        });
        toast.success('Account created! Complete your profile.');
        setStep(3);
      } catch (err: any) {
        toast.error(err.message || 'Registration failed. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (step === 3) {
      setStep(4);
      return;
    }
    setStep(prev => prev + 1);
  };

  const getDashboardRoute = () => {
    switch (form.purpose) {
      case 'seller': return '/seller-dashboard';
      case 'employer': return '/dashboard';
      case 'agent': return '/dashboard';
      case 'job_seeker': return '/user-dashboard';
      default: return '/user-dashboard';
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--background)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-2">
          <img src="/assets/china-ndogo-logo.svg" alt="China Ndogo" width={32} height={32} />
          <span className="font-extrabold text-sm tracking-tight" style={{ color: 'var(--primary)' }}>CHINA NDOGO</span>
        </div>
        {step < 4 && (
          <button onClick={() => router.push('/login')} className="text-xs font-600" style={{ color: 'var(--muted-foreground)' }}>
            Sign in instead
          </button>
        )}
      </div>

      {/* Progress */}
      {step < 4 && (
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center gap-2 mb-3">
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className="flex items-center gap-1.5">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-800 transition-all ${i + 1 <= step ? 'text-black' : 'text-muted-foreground'}`}
                    style={{ background: i + 1 < step ? 'var(--primary)' : i + 1 === step ? 'var(--primary)' : 'var(--secondary)', border: i + 1 > step ? '1px solid var(--border)' : 'none' }}>
                    {i + 1 < step ? <CheckCircle2 size={14} /> : i + 1}
                  </div>
                  <span className={`text-xs font-600 hidden sm:block ${i + 1 === step ? 'text-foreground' : 'text-muted-foreground'}`}>{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className="flex-1 h-px" style={{ background: i + 1 < step ? 'var(--primary)' : 'var(--border)' }} />}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-32">
        <div className="max-w-lg mx-auto">

          {/* Step 1: Purpose */}
          {step === 1 && (
            <div>
              <h1 className="text-2xl font-800 text-foreground mt-4 mb-1">Welcome to China Ndogo</h1>
              <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>What brings you here? We'll set up the right experience for you.</p>
              <div className="grid grid-cols-1 gap-3">
                {purposeOptions.map((opt) => {
                  const Icon = opt.icon;
                  const selected = form.purpose === opt.id;
                  return (
                    <button key={opt.id} onClick={() => update('purpose', opt.id as AccountPurpose)}
                      className="flex items-center gap-4 p-4 rounded-2xl text-left transition-all active:scale-[0.99]"
                      style={{
                        background: selected ? `${opt.color}12` : 'var(--card)',
                        border: `1.5px solid ${selected ? opt.color : 'var(--border)'}`,
                        boxShadow: selected ? `0 0 0 3px ${opt.color}20` : 'none',
                      }}>
                      <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${opt.color}18` }}>
                        <Icon size={22} style={{ color: opt.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="font-700 text-sm text-foreground">{opt.label}</div>
                        <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{opt.desc}</div>
                      </div>
                      {selected && <CheckCircle2 size={20} style={{ color: opt.color }} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Account Details */}
          {step === 2 && (
            <div>
              <h1 className="text-2xl font-800 text-foreground mt-4 mb-1">Create your account</h1>
              <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>Fill in your details to get started.</p>
              <div className="flex flex-col gap-4">
                {/* Full Name */}
                <div>
                  <label className="text-xs font-700 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Full Name *</label>
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                    <User size={16} style={{ color: 'var(--muted-foreground)' }} />
                    <input type="text" value={form.fullName} onChange={e => update('fullName', e.target.value)}
                      placeholder="Your full name" className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground" />
                  </div>
                </div>
                {/* Phone */}
                <div>
                  <label className="text-xs font-700 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Phone Number</label>
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                    <Phone size={16} style={{ color: 'var(--muted-foreground)' }} />
                    <input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)}
                      placeholder="+254 700 000 000" className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground" />
                  </div>
                </div>
                {/* Email */}
                <div>
                  <label className="text-xs font-700 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Email Address *</label>
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                    <Mail size={16} style={{ color: 'var(--muted-foreground)' }} />
                    <input type="email" value={form.email} onChange={e => update('email', e.target.value)}
                      placeholder="you@example.com" className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground" />
                  </div>
                </div>
                {/* Password */}
                <div>
                  <label className="text-xs font-700 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Password *</label>
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                    <Lock size={16} style={{ color: 'var(--muted-foreground)' }} />
                    <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => update('password', e.target.value)}
                      placeholder="Min. 6 characters" className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground" />
                    <button onClick={() => setShowPassword(!showPassword)} type="button">
                      {showPassword ? <EyeOff size={16} style={{ color: 'var(--muted-foreground)' }} /> : <Eye size={16} style={{ color: 'var(--muted-foreground)' }} />}
                    </button>
                  </div>
                </div>
                {/* Confirm Password */}
                <div>
                  <label className="text-xs font-700 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Confirm Password *</label>
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                    <Lock size={16} style={{ color: 'var(--muted-foreground)' }} />
                    <input type="password" value={form.confirmPassword} onChange={e => update('confirmPassword', e.target.value)}
                      placeholder="Repeat password" className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground" />
                  </div>
                </div>
                {/* Country */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-700 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Country</label>
                    <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                      <Globe size={16} style={{ color: 'var(--muted-foreground)' }} />
                      <input type="text" value={form.country} onChange={e => update('country', e.target.value)}
                        placeholder="Kenya" className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-700 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Location</label>
                    <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                      <MapPin size={16} style={{ color: 'var(--muted-foreground)' }} />
                      <input type="text" value={form.location} onChange={e => update('location', e.target.value)}
                        placeholder="Nairobi" className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground" />
                    </div>
                  </div>
                </div>
                {/* Terms */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <div className="mt-0.5 w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-all"
                    style={{ background: form.acceptTerms ? 'var(--primary)' : 'var(--secondary)', border: `1.5px solid ${form.acceptTerms ? 'var(--primary)' : 'var(--border)'}` }}
                    onClick={() => update('acceptTerms', !form.acceptTerms)}>
                    {form.acceptTerms && <CheckCircle2 size={13} color="#000" />}
                  </div>
                  <span className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                    I agree to the <a href="#" className="underline" style={{ color: 'var(--primary)' }}>Terms of Service</a> and <a href="#" className="underline" style={{ color: 'var(--primary)' }}>Privacy Policy</a>
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Step 3: Profile Setup */}
          {step === 3 && (
            <div>
              <h1 className="text-2xl font-800 text-foreground mt-4 mb-1">Complete your profile</h1>
              <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
                {form.purpose === 'seller' ? 'Tell buyers about your shop.' :
                  form.purpose === 'employer' ? 'Tell job seekers about your company.' :
                    form.purpose === 'job_seeker'? 'Help employers find you.' : 'A few more details to personalise your experience.'}
              </p>
              <div className="flex flex-col gap-4">
                {/* Bio */}
                <div>
                  <label className="text-xs font-700 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>
                    {form.purpose === 'seller' ? 'Shop Description' : form.purpose === 'employer' ? 'Company Description' : 'About You'}
                  </label>
                  <textarea value={form.bio || ''} onChange={e => update('bio', e.target.value)} rows={3}
                    placeholder="Tell us a bit about yourself or your business..."
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none resize-none text-foreground placeholder:text-muted-foreground"
                    style={{ background: 'var(--card)', border: '1px solid var(--border)' }} />
                </div>
                {/* Seller: Shop name */}
                {(form.purpose === 'seller' || form.purpose === 'employer' || form.purpose === 'agent') && (
                  <div>
                    <label className="text-xs font-700 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>
                      {form.purpose === 'seller' ? 'Shop / Business Name' : 'Company Name'}
                    </label>
                    <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                      <Store size={16} style={{ color: 'var(--muted-foreground)' }} />
                      <input type="text" value={form.companyName || ''} onChange={e => update('companyName', e.target.value)}
                        placeholder="Your business name" className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground" />
                    </div>
                  </div>
                )}
                {/* Job seeker: Skills */}
                {form.purpose === 'job_seeker' && (
                  <>
                    <div>
                      <label className="text-xs font-700 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Job Title / Profession</label>
                      <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                        <Briefcase size={16} style={{ color: 'var(--muted-foreground)' }} />
                        <input type="text" value={form.jobTitle || ''} onChange={e => update('jobTitle', e.target.value)}
                          placeholder="e.g. Software Engineer" className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-700 mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Key Skills</label>
                      <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                        <input type="text" value={form.skills || ''} onChange={e => update('skills', e.target.value)}
                          placeholder="e.g. React, Node.js, Python" className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground" />
                      </div>
                    </div>
                  </>
                )}
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>You can complete your profile later from your dashboard.</p>
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="flex flex-col items-center text-center py-8">
              <div className="w-24 h-24 rounded-full flex items-center justify-center mb-6" style={{ background: 'rgba(0,200,83,0.12)', border: '2px solid rgba(0,200,83,0.3)' }}>
                <CheckCircle2 size={48} style={{ color: 'var(--primary)' }} />
              </div>
              <h1 className="text-2xl font-800 text-foreground mb-2">You're all set! 🎉</h1>
              <p className="text-sm mb-8 max-w-xs" style={{ color: 'var(--muted-foreground)' }}>
                Welcome to China Ndogo. Your account has been created successfully.
              </p>

              {/* Quick setup checklist */}
              <div className="w-full max-w-sm rounded-2xl p-4 mb-6 text-left" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                <h3 className="text-sm font-700 text-foreground mb-3">Quick Setup Checklist</h3>
                {[
                  { label: 'Account created', done: true },
                  { label: 'Complete your profile', done: false },
                  { label: form.purpose === 'seller' ? 'Post your first product' : form.purpose === 'employer' ? 'Post your first job' : 'Browse listings', done: false },
                  { label: 'Install the app', done: false },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 py-2">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0`}
                      style={{ background: item.done ? 'var(--primary)' : 'var(--secondary)', border: item.done ? 'none' : '1.5px solid var(--border)' }}>
                      {item.done && <CheckCircle2 size={12} color="#000" />}
                    </div>
                    <span className={`text-sm ${item.done ? 'line-through' : ''}`} style={{ color: item.done ? 'var(--muted-foreground)' : 'var(--foreground)' }}>{item.label}</span>
                  </div>
                ))}
              </div>

              {/* Install prompt */}
              <div className="w-full max-w-sm rounded-2xl p-4 mb-6" style={{ background: 'rgba(0,200,83,0.06)', border: '1px solid rgba(0,200,83,0.2)' }}>
                <div className="flex items-center gap-3">
                  <Download size={20} style={{ color: 'var(--primary)' }} />
                  <div className="flex-1">
                    <div className="text-sm font-700 text-foreground">Install China Ndogo</div>
                    <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Get faster access from your home screen</div>
                  </div>
                  <button className="text-xs font-700 px-3 py-1.5 rounded-lg" style={{ background: 'var(--primary)', color: '#000' }}>
                    Install
                  </button>
                </div>
              </div>

              <button onClick={() => router.push(getDashboardRoute())}
                className="w-full max-w-sm flex items-center justify-center gap-2 py-4 rounded-2xl font-700 text-base transition-all hover:opacity-90"
                style={{ background: 'var(--primary)', color: '#001A08' }}>
                Go to My Dashboard <ArrowRight size={18} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bottom CTA */}
      {step < 4 && (
        <div className="fixed bottom-0 left-0 right-0 p-4 border-t" style={{ background: 'var(--background)', borderColor: 'var(--border)' }}>
          <div className="max-w-lg mx-auto flex gap-3">
            {step > 1 && (
              <button onClick={() => setStep(prev => prev - 1)}
                className="flex items-center gap-2 px-5 py-3.5 rounded-2xl font-600 text-sm transition-all"
                style={{ background: 'var(--secondary)', border: '1px solid var(--border)', color: 'var(--foreground)' }}>
                <ChevronLeft size={16} /> Back
              </button>
            )}
            <button onClick={handleNext} disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl font-700 text-sm transition-all disabled:opacity-60"
              style={{ background: 'var(--primary)', color: '#001A08' }}>
              {loading ? (
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                <>{step === 2 ? 'Create Account' : step === 3 ? 'Complete Setup' : 'Continue'} <ChevronRight size={16} /></>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OnboardingPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--background)' }}><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
      <OnboardingContent />
    </Suspense>
  );
}
